import { isTouchMacUserAgent } from '@/utils/user-agent/isTabletUserAgent';

const osPatternMapping: [RegExp, string][] = [
    [/android/i, 'Android'],
    [/iphone|ipod|ipad/i, 'iOS'],
    [/cros/i, 'Chrome OS'],
    [/windows/i, 'Windows'],
    [/macintosh|mac os x/i, 'Mac'],
    [/linux|freebsd|openbsd|sunos/i, 'Linux'],
];

export type OSNameSignals = {
    maxTouchPoints?: number;
    platform?: string;
    userAgentDataPlatform?: string;
};

/** Desktop OS named by navigator.platform ("MacIntel", "Win32") or UA-CH platform ("macOS", "Windows"). */
const getDesktopOSFromPlatform = (platform?: string): 'Mac' | 'Windows' | undefined => {
    if (!platform) {
        return undefined;
    }
    if (/^mac/i.test(platform)) {
        return 'Mac';
    }
    if (/^win/i.test(platform)) {
        return 'Windows';
    }
    return undefined;
};

export const getOSName = (userAgent?: string, signals: OSNameSignals = {}): string | undefined => {
    const { maxTouchPoints = 0, platform, userAgentDataPlatform } = signals;

    if (typeof userAgent !== 'string' || !userAgent.trim()) {
        return undefined;
    }

    // iPad on its default UA, or an iPhone in desktop mode, both report a plain Macintosh UA;
    // only the touch-point signal tells them apart from a real Mac (see isTabletUserAgent.ts).
    if (isTouchMacUserAgent(userAgent, maxTouchPoints)) {
        // Chromium touch emulation (DevTools responsive mode) fakes that same signature on a
        // desktop host but leaves UA-CH untouched, and no real iPad browser implements userAgentData.
        return getDesktopOSFromPlatform(userAgentDataPlatform) ?? 'iOS';
    }

    const uaOSName = osPatternMapping.find(([pattern]) => pattern.test(userAgent))?.[1];

    // An Android UA on Mac/Windows hardware is impossible outside UA spoofing;
    // Linux platforms stay Android since real Android devices report "Linux armv8l" etc.
    if (uaOSName === 'Android') {
        return getDesktopOSFromPlatform(platform) ?? uaOSName;
    }

    return uaOSName;
};
